import React from "react";
import { styled } from "@mui/system";
import MUIChip from "@mui/material/Chip";
import { ChipProps as ChipPropsMUI } from "@mui/material/Chip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { ExtendedTypography } from "@vapor/react-extended";
import { Stack } from "@vapor/react-material";
export interface ChipProps extends ChipPropsMUI {}

let warnedOnce = false;

const warn = () => {
  if (!warnedOnce) {
    console.warn(
      [
        "Vapor: The `status` prop of VaporTag component has been removed",
        "",
        "For more information you may check Vapor Custom CHANGELOG https://biosphere.teamsystem.com/vapor/react/vapor-custom/-/blob/main/CHANGELOG.md."
      ].join("\n")
    );
  }
  warnedOnce = true;
};

declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    standard: true;
    ghost: true;
    default: true;
  }
}

export type VaporTagProps = {
  /**
   * 3 variants to use
   * @default default
   */
  variant?: "default" | "standard" | "ghost";
  /**
   * Every `type` value represent a color which is applied to Tag component
   * @default russianViolet
   */
  type?:
    | "blueSapphire"
    | "islamicGreen"
    | "russianViolet"
    | "maximumPurple"
    | "mediumSlateBlue"
    | "prune"
    | "royalFuchsia"
    | "rustyRed";
  /**
   * This is a description which is applied to Filter Tags
   */
  description?: string;
  /**
   * Icon element.
   */
  icon?: React.ReactElement;
  /**
   * The content of the component.
   */
  label?: React.ReactNode;
  /**
   * Callback fired when the delete icon is clicked.
   * If set, the delete icon will be shown.
   */
  onDelete?: React.EventHandler<any>;

  size?: "small" | "medium";
};

export const VaporTag: (props: VaporTagProps) => JSX.Element = styled(
  React.forwardRef<HTMLDivElement, VaporTagProps>((props, ref) => {
    const { variant = "default" } = props;
    props.hasOwnProperty("status") && warn();
    return (
      <MUIChip
        ref={ref}
        {...props}
        onDelete={props.onDelete && props.onDelete}
        deleteIcon={props.onDelete && <FontAwesomeIcon icon={faXmark} />}
        label={
          props.description && props.label ? (
            <Stack direction="row" alignItems="center" gap={0.5}>
              {props.label}
              <ExtendedTypography
                variant="subtitle"
                color="primary.textTitleColor"
                sx={{ fontSize: 14 }}
              >
                {props.description}
              </ExtendedTypography>
            </Stack>
          ) : props.description === undefined ? (
            props.label
          ) : (
            <ExtendedTypography
              variant="subtitle"
              color="primary.textTitleColor"
              sx={{ fontSize: 14 }}
            >
              {props.description}
            </ExtendedTypography>
          )
        }
      ></MUIChip>
    );
  }),
  {
    name: "VaporTag",
    slot: "Root",
    overridesResolver: (props, styles) => []
  }
)(({ theme, variant = "default", type, size = "" }) => ({
  ...(variant === "default" && {
    "&.MuiChip-root.MuiChip-root": {
      height: 24,
      borderRadius: 4,
      fontFamily: "Cairo",
      fontSize: 12,
      fontWeight: 500,
      fontStyle: "normal",
      background: theme.palette.primary.tertiaryBackgroundActiveColor,
      color: theme.palette.primary.tertiaryTextHoverColor,
      " .MuiChip-deleteIcon": {
        fontSize: 12,
        color: theme.palette.primary.tertiaryTextHoverColor,
        padding: 6
      }
    }
  }),
  ...(variant === "standard" && {
    "&.MuiChip-root.MuiChip-root": {
      borderRadius: 4,
      padding: "4px 8px",
      height: size === "small" ? 18 : 24,
      background:
        {
          blueSapphire: theme.palette.primary.accentBlueSapphire,
          islamicGreen: theme.palette.primary.accentIslamicGreen,
          russianViolet: theme.palette.primary.accentRussianViolet,
          maximumPurple: theme.palette.primary.accentMaximumPurple,
          mediumSlateBlue: theme.palette.primary.accentMediumSlateBlue,
          prune: theme.palette.primary.accentPrune,
          royalFuchsia: theme.palette.primary.accentRoyalFuchsia,
          rustyRed: theme.palette.primary.accentRustyRed
        }[type] || theme.palette.primary.baseInfoColor,
      "& .MuiChip-label": {
        ...theme.typography["caption"],
        color: theme.palette.primary.baseWhiteColor,
        padding: 0
      },
      "& .MuiChip-deleteIcon": {
        color: theme.palette.primary.baseWhiteColor,
        fontSize: 12,
        marginLeft: 8
      },
      "& .MuiChip-icon": {
        color: theme.palette.primary.baseWhiteColor,
        margin: 0,
        marginRight: 8,
        fontSize: 16
      },
      ":hover": {
        "& .MuiChip-icon": {
          color: theme.palette.primary.baseWhiteColor,
          margin: 0,
          marginRight: 8,
          fontSize: 16
        },
        "& .MuiChip-label": {
          color: theme.palette.primary.baseWhiteColor
        }
      }
    }
  }),
  ...(variant === "ghost" && {
    "&.MuiChip-root.MuiChip-root": {
      background: "transparent",
      border:
        "1px solid " +
          {
            blueSapphire: theme.palette.primary.accentBlueSapphire,
            islamicGreen: theme.palette.primary.accentIslamicGreen,
            russianViolet: theme.palette.primary.accentRussianViolet,
            maximumPurple: theme.palette.primary.accentMaximumPurple,
            mediumSlateBlue: theme.palette.primary.accentMediumSlateBlue,
            prune: theme.palette.primary.accentPrune,
            royalFuchsia: theme.palette.primary.accentRoyalFuchsia,
            rustyRed: theme.palette.primary.accentRustyRed
          }[type] || "#361D5B",
      "& .MuiChip-deleteIcon": {
        color:
          {
            blueSapphire: theme.palette.primary.accentBlueSapphire,
            islamicGreen: theme.palette.primary.accentIslamicGreen,
            russianViolet: theme.palette.primary.accentRussianViolet,
            maximumPurple: theme.palette.primary.accentMaximumPurple,
            mediumSlateBlue: theme.palette.primary.accentMediumSlateBlue,
            prune: theme.palette.primary.accentPrune,
            royalFuchsia: theme.palette.primary.accentRoyalFuchsia,
            rustyRed: theme.palette.primary.accentRustyRed
          }[type] || theme.palette.primary.accentRussianViolet,
        fontSize: 12,
        marginLeft: 8
      },
      borderRadius: 4,
      padding: "4px 8px",
      height: size === "small" ? 18 : 24,
      "& .MuiChip-label": {
        ...theme.typography["caption"],
        color:
          {
            blueSapphire: theme.palette.primary.accentBlueSapphire,
            islamicGreen: theme.palette.primary.accentIslamicGreen,
            russianViolet: theme.palette.primary.accentRussianViolet,
            maximumPurple: theme.palette.primary.accentMaximumPurple,
            mediumSlateBlue: theme.palette.primary.accentMediumSlateBlue,
            prune: theme.palette.primary.accentPrune,
            royalFuchsia: theme.palette.primary.accentRoyalFuchsia,
            rustyRed: theme.palette.primary.accentRustyRed
          }[type] || theme.palette.primary.accentRussianViolet,
        padding: 0
      },
      ":hover": {
        background: "transparent",
        "& .MuiChip-label": {
          ...theme.typography["caption"],
          color:
            {
              blueSapphire: theme.palette.primary.accentBlueSapphire,
              islamicGreen: theme.palette.primary.accentIslamicGreen,
              russianViolet: theme.palette.primary.accentRussianViolet,
              maximumPurple: theme.palette.primary.accentMaximumPurple,
              mediumSlateBlue: theme.palette.primary.accentMediumSlateBlue,
              prune: theme.palette.primary.accentPrune,
              royalFuchsia: theme.palette.primary.accentRoyalFuchsia,
              rustyRed: theme.palette.primary.accentRustyRed
            }[type] || theme.palette.primary.accentRussianViolet,
          padding: 0
        }
      }
    }
  })
}));
